import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"

Chart.register(...registerables)

export default class extends Controller {
  static targets = ["canvas", "loading", "error", "noData"]

  static values = {
    instanceId: Number,
    metricType: String,
    fetchUrl: String,
    timeRange: { type: String, default: "1h" },
    refreshInterval: { type: Number, default: 60000 },
    chartType: { type: String, default: "line" }
  }

  connect() {
    this.chart = null
    this.fetchAndRender()
    this.startAutoRefresh()
  }

  disconnect() {
    this.stopAutoRefresh()
    if (this.chart) {
      this.chart.destroy()
    }
  }

  async fetchAndRender() {
    this.showLoading()

    try {
      const url = `${this.fetchUrlValue}?time_range=${this.timeRangeValue}`
      const response = await fetch(url, {
        headers: { "Accept": "application/json" }
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const data = await response.json()
      this.renderChart(data.metrics)
    } catch (error) {
      console.error("Failed to fetch metrics:", error)
      this.showError()
    }
  }

  renderChart(metrics) {
    this.hideAllStates()

    const metricData = metrics[this.metricTypeValue]
    if (!metricData || !metricData.values || metricData.values.length === 0) {
      this.showNoData()
      return
    }

    this.canvasTarget.classList.remove("hidden")

    if (this.chart) {
      this.chart.destroy()
    }

    const ctx = this.canvasTarget.getContext("2d")
    this.chart = new Chart(ctx, this.buildChartConfig(metricData))
  }

  buildChartConfig(data) {
    const colors = this.getChartColors()

    return {
      type: this.chartTypeValue,
      data: {
        labels: data.labels,
        datasets: [{
          label: this.formatMetricLabel(this.metricTypeValue),
          data: data.values,
          borderColor: colors.border,
          backgroundColor: colors.background,
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointRadius: 2,
          pointHoverRadius: 4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: "index"
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            backgroundColor: "rgba(17, 24, 39, 0.9)",
            titleColor: "#f3f4f6",
            bodyColor: "#d1d5db",
            padding: 12,
            displayColors: false,
            callbacks: {
              label: (context) => {
                return `${this.formatValue(context.raw)}`
              }
            }
          }
        },
        scales: {
          x: {
            grid: {
              color: "rgba(107, 114, 128, 0.1)"
            },
            ticks: {
              color: "#9ca3af",
              maxTicksLimit: 8
            }
          },
          y: {
            beginAtZero: this.shouldBeginAtZero(),
            grid: {
              color: "rgba(107, 114, 128, 0.1)"
            },
            ticks: {
              color: "#9ca3af",
              callback: (value) => this.formatValue(value)
            }
          }
        }
      }
    }
  }

  getChartColors() {
    const colorMap = {
      cpu_utilization: { border: "#6366f1", background: "rgba(99, 102, 241, 0.1)" },
      memory_utilization: { border: "#8b5cf6", background: "rgba(139, 92, 246, 0.1)" },
      response_time: { border: "#f59e0b", background: "rgba(245, 158, 11, 0.1)" },
      error_rate: { border: "#ef4444", background: "rgba(239, 68, 68, 0.1)" },
      throughput: { border: "#10b981", background: "rgba(16, 185, 129, 0.1)" },
      apdex: { border: "#06b6d4", background: "rgba(6, 182, 212, 0.1)" }
    }
    return colorMap[this.metricTypeValue] || { border: "#6b7280", background: "rgba(107, 114, 128, 0.1)" }
  }

  formatMetricLabel(type) {
    const labels = {
      cpu_utilization: "CPU Utilization",
      memory_utilization: "Memory Utilization",
      response_time: "Response Time",
      error_rate: "Error Rate",
      throughput: "Throughput",
      apdex: "Apdex Score"
    }
    return labels[type] || type
  }

  formatValue(value) {
    const formats = {
      cpu_utilization: `${value.toFixed(1)}%`,
      memory_utilization: `${value.toFixed(1)}%`,
      response_time: `${value.toFixed(0)}ms`,
      error_rate: `${value.toFixed(2)}%`,
      throughput: `${value.toFixed(0)} rpm`,
      apdex: value.toFixed(2)
    }
    return formats[this.metricTypeValue] || value.toString()
  }

  shouldBeginAtZero() {
    return ["cpu_utilization", "memory_utilization", "error_rate"].includes(this.metricTypeValue)
  }

  changeTimeRange(event) {
    this.timeRangeValue = event.target.value
    this.fetchAndRender()
  }

  refresh() {
    this.fetchAndRender()
  }

  startAutoRefresh() {
    if (this.refreshIntervalValue > 0) {
      this.refreshTimer = setInterval(() => {
        this.fetchAndRender()
      }, this.refreshIntervalValue)
    }
  }

  stopAutoRefresh() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
      this.refreshTimer = null
    }
  }

  showLoading() {
    this.hideAllStates()
    if (this.hasLoadingTarget) this.loadingTarget.classList.remove("hidden")
  }

  showError() {
    this.hideAllStates()
    if (this.hasErrorTarget) this.errorTarget.classList.remove("hidden")
  }

  showNoData() {
    this.hideAllStates()
    if (this.hasNoDataTarget) this.noDataTarget.classList.remove("hidden")
  }

  hideAllStates() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden")
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")
    if (this.hasNoDataTarget) this.noDataTarget.classList.add("hidden")
    if (this.hasCanvasTarget) this.canvasTarget.classList.add("hidden")
  }
}
